
import React, { useEffect, useState } from "react";
import { useParams, Link } from "react-router-dom";
import { api } from "@/lib/api";
import { Button } from "@/components/ui/button";

function fmtAnswer(v, type) {
  if (v === null || v === undefined || v === "") return "(žádná odpověď)";
  if (type === "true_false") return v === true || v === "true" ? "Pravda" : "Nepravda";
  if (Array.isArray(v)) return v.map(x => fmtAnswer(x)).join(", ");
  if (typeof v === "object") return Object.entries(v).map(([k, x]) => `${k}: ${fmtAnswer(x)}`).join("; ");
  return String(v);
}

export default function TestResult() {
  const { attemptId } = useParams();
  const [data, setData] = useState(null);
  const [error, setError] = useState(false);

  useEffect(() => {
    api.get(`/attempts/${attemptId}/result`).then((r) => setData(r.data)).catch(() => setError(true));
  }, [attemptId]);

  if (error) return <div role="alert" className="p-12">Výsledek se nepodařilo načíst. <Link to="/testy" className="underline">Zpět na testy</Link></div>;
  if (!data) {
    return <div className="min-h-screen bg-parchment flex items-center justify-center caption">Načítání výsledku…</div>;
  }

  const { attempt, test, items, ai_feedback } = data;
  const sourceFor = q => q.source_text || (q.source_text_id && items.find(it => it.question.source_text_id === q.source_text_id && it.question.source_text)?.question.source_text);
  const pct = attempt.max_possible_score
    ? Math.round((attempt.total_score / attempt.max_possible_score) * 100)
    : 0;

  const passLine = 40; // informative threshold
  const passed = pct >= passLine;

  return (
    <div className="min-h-screen bg-parchment">
      <div className="border-b border-stone bg-parchment">
        <div className="max-w-[1280px] mx-auto px-6 lg:px-12 h-16 flex items-center justify-between">
          <Link to="/" className="font-serif text-2xl text-navy">Discendo</Link>
          <Link to="/testy">
            <Button variant="outline" size="sm" className="border-stone text-ink rounded-sm hover:bg-lemon" data-testid="back-to-tests">
              Zpět na testy
            </Button>
          </Link>
        </div>
      </div>

      {/* Hero score */}
      <section className="max-w-[1280px] mx-auto px-6 lg:px-12 pt-20 pb-16">
        <p className="caption mb-6">Vyhodnocení</p>
        <h1 className="font-serif text-3xl lg:text-4xl text-ink mb-3 max-w-[40ch] leading-tight">
          {test.title}
        </h1>
        <div className="mt-12 grid grid-cols-12 gap-8 items-end">
          <div className="col-span-12 md:col-span-7">
            <p className="caption mb-3">{attempt.grading_status === "pending_review" ? "Průběžné skóre — čeká na kontrolu učitelem" : "Tvé skóre"}</p>
            <p className="font-mono tabular text-7xl lg:text-8xl text-navy leading-none" data-testid="result-score">
              {attempt.total_score}
              <span className="text-stone"> / {attempt.max_possible_score}</span>
            </p>
            <p className="caption mt-3">bodů</p>
            <p className="mt-6 text-lg text-slate">
              Úspěšnost {pct}%{attempt.grading_status === "pending_review" ? " zatím; část odpovědí čeká na kontrolu" : ""}. {passed ? "Pohyb správným směrem." : "Pojďme se podívat, kde získat body příště."}
            </p>
          </div>
          <div className="col-span-12 md:col-span-5 md:pl-8">
            <div className="grid grid-cols-2 gap-px bg-stone border border-stone">
              <Stat label="Čas" value={`${Math.floor(attempt.time_spent_seconds / 60)} min`} />
              <Stat label="Otázek" value={items.length} />
            </div>
          </div>
        </div>
      </section>

      {/* AI editorial summary */}
      {ai_feedback && (
        <section className="border-t border-stone bg-lemon/40">
          <div className="max-w-[760px] mx-auto px-6 lg:px-8 py-20">
            <p className="caption mb-6">Shrnutí výkonu</p>
            <p className="text-lg text-ink leading-[1.7] drop-cap" data-testid="ai-summary">
              {ai_feedback.overall_summary}
            </p>

            {ai_feedback.strengths?.length > 0 && (
              <div className="mt-12">
                <h3 className="font-serif text-xl text-ink mb-4">Silné stránky</h3>
                <ul className="space-y-2">
                  {ai_feedback.strengths.map((s, i) => (
                    <li key={i} className="flex justify-between text-base">
                      <span className="capitalize">{s.topic.replaceAll("_"," ")}</span>
                      <span className="font-mono tabular text-leaf">{Math.round(s.accuracy * 100)}%</span>
                    </li>
                  ))}
                </ul>
              </div>
            )}

            {ai_feedback.weaknesses?.length > 0 && (
              <div className="mt-10">
                <h3 className="font-serif text-xl text-ink mb-4">Kam zacílit příště</h3>
                <ul className="space-y-2">
                  {ai_feedback.weaknesses.map((s, i) => (
                    <li key={i} className="flex justify-between text-base">
                      <span className="capitalize">{s.topic.replaceAll("_"," ")}</span>
                      <span className="font-mono tabular text-burgundy">{Math.round(s.accuracy * 100)}%</span>
                    </li>
                  ))}
                </ul>
              </div>
            )}

            {ai_feedback.recommendations?.length > 0 && (
              <div className="mt-10">
                <h3 className="font-serif text-xl text-ink mb-4">Doporučení</h3>
                <ol className="space-y-3 list-decimal pl-6 marker:text-navy marker:font-mono">
                  {ai_feedback.recommendations.map((r, i) => (
                    <li key={i} className="text-base text-ink leading-relaxed">{r}</li>
                  ))}
                </ol>
              </div>
            )}
          </div>
        </section>
      )}

      {/* Per-question review */}
      <section className="border-t border-stone">
        <div className="max-w-[760px] mx-auto px-6 lg:px-8 py-20">
          <p className="caption mb-6">Otázky a vysvětlení</p>
          <h2 className="font-serif text-3xl text-ink mb-12">
            Projděme to spolu, <span className="font-serif-italic">krok za krokem</span>.
          </h2>

          <div className="space-y-16" data-testid="result-items">
            {items.map((it, idx) => (
              <article key={it.question.id} className="border-t border-stone pt-10">
                <div className="flex items-baseline justify-between mb-4">
                  <p className="caption">Otázka {idx + 1} · {it.question.topic_tag?.replaceAll("_"," ")}</p>
                  <p className={`font-mono tabular text-sm ${it.is_correct ? "text-leaf" : "text-burgundy"}`}>
                    {it.pending_review ? "Čeká na kontrolu" : `${it.points_awarded} / ${it.question.points} bodů`}
                  </p>
                </div>
                <h3 className="font-serif text-xl text-ink leading-snug mb-6">{it.question.question_text}</h3>

                <dl className="space-y-3 text-base">
                  <div className="flex gap-3">
                    <dt className="caption pt-1 w-28">Tvá odpověď</dt>
                    <dd className={`${it.is_correct ? "text-leaf" : "text-burgundy"}`}>
                      {fmtAnswer(it.user_answer, it.question.question_type)}
                    </dd>
                  </div>
                  <div className="flex gap-3">
                    <dt className="caption pt-1 w-28">{it.question.grading_mode === "manual" ? "Příklad" : "Správně"}</dt>
                    <dd className="text-ink">{fmtAnswer(it.question.correct_answer, it.question.question_type)}</dd>
                  </div>
                </dl>

                {it.review_comment && <p className="mb-4 text-ink">Komentář učitele: {it.review_comment}</p>}
                {sourceFor(it.question) && <p className="mb-4 whitespace-pre-wrap font-serif">{sourceFor(it.question)}</p>}
                {it.question.image_url && <img src={it.question.image_url} alt="Obrázek k úloze" className="mb-4" />}
                {["subquestions", "matching_items", "matching_choices", "ordering_parts"].map(field => it.question[field]?.map(row => <p key={field+row.label}>{row.label}: {row.text}</p>))}
                {it.question.options && <ol className="mb-4">{it.question.options.map((opt, i) => <li key={i}>{String.fromCharCode(65+i)}. {opt}</li>)}</ol>}
                {it.question.explanation && (
                  <div className="mt-6">
                    <p className="caption mb-2">Vysvětlení řešení</p>
                    <p className="text-base text-ink leading-relaxed">{it.question.explanation}</p>
                  </div>
                )}

                
                {!it.is_correct && it.ai_explanation && (
                  <div className="mt-6 pull-quote" data-testid={`ai-explanation-${idx}`}>
                    {it.ai_explanation}
                  </div>
                )}
              </article>
            ))}
          </div>

          <div className="mt-16 flex flex-wrap gap-4">
            <Link to="/testy" data-testid="cta-another-test">
              <Button className="bg-navy hover:bg-navy-dark text-parchment rounded-sm py-6 px-7">
                Další test
              </Button>
            </Link>
            <Link to="/pokrok">
              <Button variant="outline" className="border-ink text-ink rounded-sm hover:bg-lemon py-6 px-7">
                Vidět můj pokrok
              </Button>
            </Link>
          </div>
        </div>
      </section>
    </div>
  );
}

function Stat({ label, value }) {
  return (
    <div className="bg-bone px-6 py-6">
      <p className="caption mb-2">{label}</p>
      <p className="font-mono tabular text-2xl text-ink">{value}</p>
    </div>
  );
}
